import React from "react";
import { Button } from "../../../../design/button";
import { Heading, Subheading } from "../../../../design/heading";
import { Text } from "../../../../design/text";
import { Divider } from "../../../../design/divider";
import { Badge } from "../../../../design/badge";
import SettingsHeader from "../SettingsHeader";
import DropboxButton from "./components/DropboxButton";
import GoogleCalendarButton from "./components/GoogleCalendarButton";
import { useAccount } from "../../../../contexts/AccountContext";
import {
  CloudIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

interface IntegrationCardProps {
  icon: React.ReactNode;
  iconColor: string;
  name: string;
  description: string;
  isConnected: boolean;
  connectButton: React.ReactNode;
  onDisconnect?: () => void;
}

const IntegrationCard: React.FC<IntegrationCardProps> = ({
  icon,
  iconColor,
  name,
  description,
  isConnected,
  connectButton,
  onDisconnect,
}) => (
  <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
    <div className="flex items-start justify-between">
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-lg ${iconColor}`}>
          {icon}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <Subheading>{name}</Subheading>
            <Badge color={isConnected ? "green" : "zinc"}>
              {isConnected ? (
                <span className="flex items-center gap-1">
                  <CheckCircleIcon className="w-3 h-3" />
                  Connected
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <XCircleIcon className="w-3 h-3" />
                  Not connected
                </span>
              )}
            </Badge>
          </div>
          <Text className="mt-1 text-zinc-600 dark:text-zinc-400">
            {description}
          </Text>
        </div>
      </div>
      <div>
        {isConnected ? (
          <Button outline onClick={onDisconnect}>
            Disconnect
          </Button>
        ) : (
          connectButton
        )}
      </div>
    </div>
  </div>
);

const Integrations: React.FC = () => {
  const { account, integrations, deleteIntegration } = useAccount();
  const isDropboxConnected = integrations.some((i) => i.id === "dropbox");
  const isCalendarConnected = Boolean(account?.nylasGrantId);

  return (
    <>
      <SettingsHeader />

      <div className="space-y-8">
        {/* Header */}
        <div>
          <Heading>Integrations</Heading>
          <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
            Connect VisitProof with other services to enhance your workflow.
          </Text>
        </div>

        {/* Integration Cards */}
        <div className="space-y-4">
          <IntegrationCard
            icon={<CloudIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />}
            iconColor="bg-blue-50 dark:bg-blue-900/20"
            name="Dropbox"
            description="Automatically sync visit reports and documents to your Dropbox account for easy sharing and backup."
            isConnected={isDropboxConnected}
            connectButton={<DropboxButton />}
            onDisconnect={() => deleteIntegration("dropbox")}
          />

          <IntegrationCard
            icon={<CalendarIcon className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />}
            iconColor="bg-emerald-50 dark:bg-emerald-900/20"
            name="Google Calendar"
            description="Sync your scheduled visits with Google Calendar to keep track of appointments across all your devices."
            isConnected={isCalendarConnected}
            connectButton={<GoogleCalendarButton />}
            onDisconnect={() => deleteIntegration("google_calendar")}
          />
        </div>

        <Divider />

        {/* Info Section */}
        <div className="p-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/50">
          <Subheading className="mb-2">About Integrations</Subheading>
          <Text className="text-sm text-zinc-600 dark:text-zinc-400">
            Integrations allow VisitProof to connect with external services to automate tasks
            and keep your data synchronized. Connected integrations can be disconnected at any time.
          </Text>
        </div>
      </div>
    </>
  );
};

export default Integrations;
